/**
 * External dependencies
 */
import React from 'react';
import { Box, Stack, Text } from '@towersystems/towersystems-design-system';
import { get } from 'lodash';
import { connect } from 'react-redux';

/**
 * Internal dependencies
 */
import AnnotatedSection from 'components/annotated-section';
import Card from 'components/card';
import FormInputCheckbox from 'components/forms/form-checkbox';
import FormLabel from 'components/forms/form-label';
import FormNumberInput from 'components/forms/form-number-input';

const BigCommercePreferences = ({ saveConfigValue, saveConfigValueInput, values }) => {
	return (
		<Stack space='xlarge' dividers>
			<AnnotatedSection>
				<AnnotatedSection.Annotation
					title='Product'
					description='Choose your preferences when sending product data.'
				/>
				<AnnotatedSection.Content>
					<Stack space='xlarge'>
						<Card>
							<Card.Section>
								<FormInputCheckbox
									label='Update product title'
									onChange={saveConfigValue}
									name='updateProductName'
									checked={get(values, 'updateProductName', false)}
								>
									When ticked the Description from Retailer will update Product Name/Title in your
									ecommerce platform
								</FormInputCheckbox>
							</Card.Section>
							<Card.Section>
								<FormInputCheckbox
									label='Update product description'
									onChange={saveConfigValue}
									name='updateProductDescription'
									checked={get(values, 'updateProductDescription', false)}
								>
									When ticked the Web Description from Retailer will be used to update Description in
									your ecommerce platform.
								</FormInputCheckbox>
							</Card.Section>
							<Card.Section>
								<FormInputCheckbox
									label='Update product brand'
									onChange={saveConfigValue}
									name='updateProductBrand'
									checked={get(values, 'updateProductBrand', false)}
								>
									Tags prefixed with Brand_ will be added to the Brand field in BigCommerce.
								</FormInputCheckbox>
							</Card.Section>
							<Card.Section>
								<FormInputCheckbox
									label='Update product dimensions'
									onChange={saveConfigValue}
									name='updateProductDimensions'
									checked={get(values, 'updateProductDimensions', false)}
								>
									When ticked the product dimensions from Retailer attribute will update the product
									shipping dimensions in your ecommerce platform.
									<br />
									<br />
									PLEASE NOTE: The dimensions in your Retailer classifications must be named as
									LENGTH, WIDTH and HEIGHT.
								</FormInputCheckbox>
							</Card.Section>
							<Card.Section>
								<FormInputCheckbox
									label='Update product price'
									onChange={saveConfigValue}
									name='updateProductPrice'
									checked={get(values, 'updateProductPrice', false)}
								>
									When ticked the chosen price from Retailer (see inventory and pricing below) will be
									used to update the product price in your ecommerce platform.
								</FormInputCheckbox>
							</Card.Section>
							<Card.Section>
								<FormInputCheckbox
									label='Update product categories'
									onChange={saveConfigValue}
									name='updateProductCategories'
									checked={get(values, 'updateProductCategories', false)}
								>
									When ticked, categories and departments from Retailer will be updated on
									BigCommerce.
								</FormInputCheckbox>
							</Card.Section>
							<Card.Section>
								<FormInputCheckbox
									label='Use Talink Variant SKU'
									onChange={saveConfigValue}
									name='useTalinkProductSku'
									checked={get(values, 'useTalinkProductSku', false)}
								>
									When ticked, main variant product sku on BigCommerce will use sku-1 instead of
									sku_1.
								</FormInputCheckbox>
							</Card.Section>
							<Card.Section>
								<FormInputCheckbox
									label='Update custom fields'
									onChange={saveConfigValue}
									name='updateCustomFields'
									checked={get(values, 'updateCustomFields', false)}
								>
									When ticked, product attributes will update custom fields on BigCommerce.
								</FormInputCheckbox>
							</Card.Section>
							<Card.Section>
								<FormInputCheckbox
									label='Update metafields'
									onChange={saveConfigValue}
									name='updateMetafields'
									checked={get(values, 'updateMetafields', false)}
								>
									When ticked, product attributes will update metafields on BigCommerce.
								</FormInputCheckbox>
							</Card.Section>
						</Card>
					</Stack>
				</AnnotatedSection.Content>
			</AnnotatedSection>
			<AnnotatedSection>
				<AnnotatedSection.Annotation
					title='Department'
					description='Choose your preferences when sending department data.'
				/>
				<AnnotatedSection.Content>
					<Card>
						<Card.Section>
							<FormInputCheckbox
								label='Update department title'
								onChange={saveConfigValue}
								name='updateDepartmentName'
								checked={get(values, 'updateDepartmentName', false)}
							>
								When ticked the Department Name from Retailer will be used to update the corresponding
								Category in your ecommerce platform.
							</FormInputCheckbox>
						</Card.Section>
					</Card>
				</AnnotatedSection.Content>
			</AnnotatedSection>
			<AnnotatedSection>
				<AnnotatedSection.Annotation
					title='Category'
					description='Choose your preferences when sending category data.'
				/>
				<AnnotatedSection.Content>
					<Card>
						<Card.Section>
							<FormInputCheckbox
								label='Update category title'
								onChange={saveConfigValue}
								name='updateCategoryName'
								checked={get(values, 'updateCategoryName', false)}
							>
								When ticked the Category Name from Retailer will be used to update the corresponding
								Category in your ecommerce platform.
							</FormInputCheckbox>
						</Card.Section>
					</Card>
				</AnnotatedSection.Content>
			</AnnotatedSection>
			<AnnotatedSection>
				<AnnotatedSection.Annotation
					title='Inventory & Pricing'
					description='Choose your preferences when sending inventory and pricing data.'
				/>
				<AnnotatedSection.Content>
					<Stack space='large'>
						<Card>
							<Card.Section>
								<FormInputCheckbox
									label='Update product inventory levels'
									onChange={saveConfigValue}
									name='updateInventory'
									checked={get(values, 'updateInventory', false)}
								>
									When ticked, the Stock on Hand value from Retailer will be used as the inventory
									level in your ecommerce platform
								</FormInputCheckbox>
							</Card.Section>
							<Card.Section>
								<FormInputCheckbox
									label='TWC Track product inventory levels'
									onChange={saveConfigValue}
									name='trackInventory'
									checked={get(values, 'trackInventory', false)}
								>
									When ticked, the Stock on Hand value from Retailer will be subtracted by the
									reported, but not yet imported, TWC sales 
								</FormInputCheckbox>
							</Card.Section>
							<Card.Section>
								<FormInputCheckbox
									label='Round down QOH To Whole Number'
									onChange={saveConfigValue}
									name='roundDownInventory'
									checked={get(values, 'roundDownInventory', false)}
								>
									When ticked, the Stock on Hand value from Retailer will be rounded down in your
									ecommerce platform if they are in decimal
								</FormInputCheckbox>
							</Card.Section>
							<Card.Section>
								<FormInputCheckbox
									label='Use static quantity'
									onChange={saveConfigValue}
									name='useStaticQuantity'
									checked={get(values, 'useStaticQuantity', false)}
								>
									When ticked, if the StaticQuantity received from Retailer is true, the inventory for
									the product will not be tracked in your ecommerce platform
								</FormInputCheckbox>
							</Card.Section>
						</Card>
						<Card>
							<Card.Section>
								<FormInputCheckbox
									label='Use web price'
									onChange={saveConfigValue}
									name='useWebPrice'
									checked={get(values, 'useWebPrice', false)}
								>
									When ticked the Retailer Web Price will be used as the sell price of the item. If
									unticked the system will use the Retailer Retail Price.
								</FormInputCheckbox>
							</Card.Section>
							<Card.Section>
								<FormInputCheckbox
									label='Use catalogue price'
									onChange={saveConfigValue}
									name='useCataloguePrice'
									checked={get(values, 'useCataloguePrice', false)}
								>
									When ticked, the Retailer Stock Item Catalogue price will be used in BigCommerce as
									the Retail Price. The regular price, either the web or retail price, will be set as
									the compare at price.
								</FormInputCheckbox>
							</Card.Section>
						</Card>
						<Card>
							<Card.Section>
								<Box className='card__section-outofstockqty'>
									<FormLabel>Out of Stock Quantity:</FormLabel>
									<FormNumberInput
										name='outOfStockQty'
										onChange={saveConfigValueInput}
										value={get(values, 'outOfStockQty')}
									/>
									<Text className='form-text-input__additional-content'>
										The Quantity entered here will be used as out of stock quantity. Any qty below
										this value will be marked as out of stock on your ecommerce platform.
									</Text>
								</Box>
							</Card.Section>
							<Card.Section>
								<FormInputCheckbox
									label='Unpublish out of stock'
									onChange={saveConfigValue}
									name='unpublishOutOfStock'
									checked={get(values, 'unpublishOutOfStock', false)}
								>
									When ticked, products with chosen inventory will be unpublished or unavailable for
									purchase on your ecommerce platform.
								</FormInputCheckbox>
							</Card.Section>
						</Card>
					</Stack>
				</AnnotatedSection.Content>
			</AnnotatedSection>

			<AnnotatedSection>
                <AnnotatedSection.Annotation
                    title='Blog Posts'
                    description='Choose your preferences when sending blog posts data.'
                />
                <AnnotatedSection.Content>
                    <Card>
                        <Card.Section>
                            <FormInputCheckbox
                                label='Send Blog Posts'
                                onChange={saveConfigValue}
                                name='sendBlogPosts'
                                checked={get(values, 'sendBlogPosts', false)}
                            >
                                When ticked, Blog posts from Retailer will be sent to your ecommerce platform.
                            </FormInputCheckbox>
                        </Card.Section>

                    </Card>
                </AnnotatedSection.Content>
            </AnnotatedSection>
		</Stack>
	);
};

export default connect((state, ownProps) => ({}), {})(BigCommercePreferences);
